import React, { useCallback, useEffect, useState } from "react"
import * as Yup from "yup"
import { useRecoilValue, useSetRecoilState, useRecoilState } from "recoil"
import { brandingSelector } from "../../../../recoil/atoms/room"
import { FaRegTrashAlt } from "react-icons/fa"
import type { StartScreenButtonsProps } from "./start-screen-buttons.interface"
import {
  AccordionItem,
  Flex,
  Accordion,
  Box,
  AccordionIcon,
  AccordionPanel,
  Spinner,
  Center
} from "@chakra-ui/react"
import {
  Wrapper,
  Title,
  Text,
  InputWrapper,
  DeleteButton,
  DeleteIcon
} from "../../../styles/common.styled"
import { DropdownButton } from "./start-screen-buttons.styled"
import {
  watchPartyHistoryLinksSelector,
  watchPartyHistoryLinksAtom
} from "../../../../recoil/atoms/watchPartyHistoryLinks"
import { ModerationTextTip } from "../../../servicesPanel/moderation/moderation.styled"
import { SDKContext } from "../../../../context/SDKContext"
import { localUserIdAtom, usersSelector } from "../../../../recoil/atoms/users"
import { watchPartyLoopStatusAtom } from "../../../../recoil/atoms/watchPartyScreen"
import { CommonInput } from "../../../common/fields"
import { SecondaryButton } from "../../../common/buttons/secondary"
import { CustomCheckbox } from "../../../common/checkbox"
import { DOMAIN_URL } from "../../../../constants"
import { playerPropsSelector } from "../../../../recoil/atoms/player"
import { ConsoleLogger } from "../../../../util/logger"
import useWindowSize from "../../../../hooks/useWindowSize"
import { useTranslation } from "react-i18next"
import { shoppableMNPVideoAtom } from "../../../../recoil/atoms/shoppableVideos"
import { Tooltip } from "../../../common/tooltip"
import { fileConversionsAtom } from "../../../../recoil/atoms/fileConversion"
import { whiteboardShareUserAtom } from "../../../../recoil/atoms/whiteboard"
import useConfig from "src/hooks/useConfig"

const logger = new ConsoleLogger()

const StartScreenButtons: React.FC<StartScreenButtonsProps> = ({
  toggleScreenShare,
  screenShareEnabled,
  toggleWhiteboardShare,
  closeModal,
  toggleWatchParty
}) => {
  const { t } = useTranslation()
  const { whiteboard } = useConfig()
  const meetingClient = React.useContext(SDKContext)
  const [text, setText] = useState<string>("")
  const [whiteboardWebsiteLink, setWhiteboardWebsiteLink] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [whiteboardLinkError, setWhiteboardLinkError] = useState<string>("")
  const { screenSize } = useWindowSize()
  const isSmallWindowHeight = React.useMemo(
    () => 770 > screenSize.height || screenSize.width < 1024,
    [screenSize.height, screenSize.width]
  )
  const roomStyles = useRecoilValue(brandingSelector)
  const watchPartyHistoryLinks = useRecoilValue(watchPartyHistoryLinksAtom)
  const setWatchPartyHistoryLinks = useSetRecoilState(
    watchPartyHistoryLinksSelector
  )

  const whiteboardShareUser = useRecoilValue(whiteboardShareUserAtom)
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  logger.log("watchPartyHistoryLinks", watchPartyHistoryLinks)
  const [isLoop, setIsLoop] = useRecoilState(watchPartyLoopStatusAtom)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const _localUserId = useRecoilValue(localUserIdAtom)
  const _localUser = useRecoilValue(usersSelector(_localUserId))
  const fileConversions = useRecoilValue(fileConversionsAtom)

  const schema = Yup.string()
    .url(t("This must be a valid URL"))
    .test(
      "InsertALinkToAnMp4OrMov",
      t("Insert a link to an mp3, mp4 or mov"),
      function () {
        return (
          text.endsWith(".mov") ||
          text.endsWith(".mp4") ||
          text.endsWith(".mp3")
        )
      }
    )
    .max(1024, "max. 1024 char")
    .test("nospace", t("Text is empty"), function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .nullable()

  const sendLink = async () => {
    try {
      setShoppableMNPVideo({
        srcShoppableMNP: "",
        enabledShoppableMNP: false,
        idShoppableMNP: "",
        typeShoppableMNP: ""
      })
      const data = await schema.validate(text)

      meetingClient?.sendLinkWatchParty(data, _localUser.id)

      toggleWatchParty(data)

      meetingClient?.sendStartingWatchParty(_localUser.id)
      setPlayerProps({ paused: true })
      closeModal()
    } catch (e) {
      setError(e.message)
    }
  }

  const whiteboardLinkSchema = Yup.string()
    .url(t("This must be a valid URL"))
    .max(1024, "max. 1024 char")
    .test("nospace", t("Text is empty"), function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .nullable()

  const fileUploadProgress = fileConversions.conversions.filter(conv => conv.status == "Converting" || conv.status == "Waiting")[0]?.convertedPercentage;

  const addPDFToWhiteboard = async () => {
    try {
      const data = await whiteboardLinkSchema.validate(whiteboardWebsiteLink)

      // check if there's an ongoing file conversion, if so: cancel it
      fileConversions.conversions
        .filter(conv => conv.status == "Converting" || conv.status == "Waiting")
        .forEach(conv =>
          meetingClient?.cancelFileConversion(conv.uuid, _localUserId)
        )

      // start this file conversion
      meetingClient?.startFileConversion(data, _localUser.id)
    } catch (e) {
      setWhiteboardLinkError(e.message)
    }
  }

  const handleWatchPartyCurrentLink = src => {
    setPlayerProps({ paused: true })
    toggleWatchParty(src)

    meetingClient?.sendStartingWatchParty(_localUserId)
    closeModal()
  }

  const handleDeleteCurrentLink = id => {
    meetingClient?.deleteLinkWatchParty(id)
    setWatchPartyHistoryLinks({
      type: "DELETE",
      payload: {
        id: id
      }
    })
  }

  return (
    <Wrapper
      style={{
        maxHeight: whiteboardShareUser != null ? "600px" : "486px",
        width: isSmallWindowHeight ? "300px" : "100%"
      }}
      roomStyles={roomStyles}
    >
      <Title isSmallWindowHeight={isSmallWindowHeight} roomStyles={roomStyles}>
        {t("Share your media")}
      </Title>
      <Text
        isSmallWindowHeight={isSmallWindowHeight}
        style={{ marginBottom: 0 }}
        roomStyles={roomStyles}
      >
        {t("Insert a link to an mp3, mp4 or mov")}
      </Text>
      <ModerationTextTip
        roomStyles={roomStyles}
        isSmallWindowHeight={isSmallWindowHeight}
      >
        {t(
          "For the best audience experience, audio should not be heard through external speakers when sharing media."
        )}
      </ModerationTextTip>
      <InputWrapper
        isSmallWindowHeight={isSmallWindowHeight}
        style={{ alignItems: "normal" }}
      >
        <CommonInput
          type="text"
          fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
          height={isSmallWindowHeight ? "2rem" : "2.5rem"}
          placeholder="https://..."
          value={text}
          onChange={e => setText(e.target.value)}
          id={"watchid"}
          error={error}
          style={{ alignItems: "normal" }}
        />
      </InputWrapper>
      <Flex>
        <CustomCheckbox
          colorscheme="gray"
          fontSize={isSmallWindowHeight ? "0.7rem !important" : "1rem"}
          marginBottom="0.4rem"
          isChecked={isLoop}
          onChange={() => setIsLoop(prev => !prev)}
          label={t("Loop video")}
        />
      </Flex>
      <SecondaryButton
        text={"Share media"}
        onClick={sendLink}
        marginBottom={isSmallWindowHeight ? "0.3rem" : "0.4rem"}
        fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
        height={isSmallWindowHeight ? "2rem" : "2.5rem"}
        disabled={text.length === 0}
      />

      <Accordion
        allowToggle
        marginBottom={isSmallWindowHeight ? "0.2rem" : "0.4rem"}
        pos="relative"
      >
        <AccordionItem border="none">
          <DropdownButton
            isSmallWindowHeight={isSmallWindowHeight}
            roomStyles={roomStyles}
            _focus={{}}
          >
            <Box flex="1">
              <Box paddingLeft="20px">{t("Previously shared media")}</Box>
            </Box>
            <AccordionIcon />
          </DropdownButton>
          <AccordionPanel
            borderStyle={"none"}
            padding={isSmallWindowHeight ? "2px 0px" : "10px 0 10px 0"}
            borderRadius="10px"
            disabled={true}
          >
            <Flex
              width={isSmallWindowHeight ? "270px" : "100%"}
              flexDirection={"column"}
              maxH={"100px"}
              overflowY={"auto"}
            >
              {watchPartyHistoryLinks
                .slice(-9)
                .reverse()
                .map(item => (
                  <Flex width="100%" key={item?.id}>
                    <Tooltip label={item?.url}>
                      <Flex
                        paddingLeft={"10px"}
                        display="block"
                        margin="auto"
                        alignItems={"center"}
                        width="100%"
                        textOverflow="ellipsis"
                        whiteSpace={"nowrap"}
                        overflow="hidden"
                        cursor="pointer"
                        opacity="0.75"
                        fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
                        color={
                          roomStyles?.servicePanelElementPrimaryText &&
                          roomStyles?.servicePanelElementPrimaryText
                        }
                        onClick={() => handleWatchPartyCurrentLink(item?.url)}
                        _hover={{
                          color:
                            roomStyles?.servicePanelElementPrimaryText ??
                            "#e9ecef",
                          opacity: 1
                        }}
                      >
                        {item?.url}
                      </Flex>
                    </Tooltip>
                    <DeleteButton
                      isSmallWindowHeight={isSmallWindowHeight}
                      roomStyles={roomStyles}
                      onClick={() => handleDeleteCurrentLink(item?.id)}
                    >
                      <DeleteIcon as={FaRegTrashAlt} roomStyles={roomStyles} />
                    </DeleteButton>
                  </Flex>
                ))}
              <Flex width="100%">
                <Tooltip label={"10 Second Countdown"}>
                  <Flex
                    paddingLeft={"10px"}
                    display="block"
                    margin="auto"
                    alignItems={"center"}
                    width="100%"
                    textOverflow="ellipsis"
                    whiteSpace={"nowrap"}
                    overflow="hidden"
                    cursor="pointer"
                    fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
                    opacity="0.75"
                    color={
                      roomStyles?.servicePanelElementPrimaryText &&
                      roomStyles?.servicePanelElementPrimaryText
                    }
                    onClick={() =>
                      handleWatchPartyCurrentLink(
                        DOMAIN_URL + "/assets/img/10SecCountdown.mp4"
                      )
                    }
                    _hover={{
                      color:
                        roomStyles?.servicePanelElementPrimaryText ?? "#e9ecef",
                      opacity: 1
                    }}
                  >
                    {"10 Second Countdown"}
                  </Flex>
                </Tooltip>
                <DeleteButton
                  isSmallWindowHeight={isSmallWindowHeight}
                  type="button"
                  roomStyles={roomStyles}
                  onClick={() =>
                    handleWatchPartyCurrentLink(
                      DOMAIN_URL + "/assets/img/10SecCountdown.mp4"
                    )
                  }
                ></DeleteButton>
              </Flex>
            </Flex>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <Title isSmallWindowHeight={isSmallWindowHeight} roomStyles={roomStyles}>
        {t("Share your screen")}
      </Title>
      <Text isSmallWindowHeight={isSmallWindowHeight} roomStyles={roomStyles}>
        {t("Click to share your screen, a tab or application")}
      </Text>
      <SecondaryButton
        text={screenShareEnabled ? "Stop screen" : "Share screen"}
        fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
        height={isSmallWindowHeight ? "2rem" : "2.5rem"}
        type="button"
        marginBottom={"5px"}
        onClick={toggleScreenShare}
      />
      {whiteboard && (
        <>
          <Title
            isSmallWindowHeight={isSmallWindowHeight}
            roomStyles={roomStyles}
          >
            {t("Share a whiteboard")}
          </Title>
          <SecondaryButton
            text={
              whiteboardShareUser != null
                ? "Stop whiteboard"
                : "Share whiteboard"
            } // TODO: use state
            fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
            height={isSmallWindowHeight ? "2rem" : "2.5rem"}
            type="button"
            onClick={toggleWhiteboardShare}
          />
        </>
      )}

      {whiteboardShareUser != null && whiteboard && (
        <>
          <Box marginBottom={"5px"}></Box>
          <Title
            isSmallWindowHeight={isSmallWindowHeight}
            roomStyles={roomStyles}
          >
            {t("Add PDF to whiteboard")}
          </Title>
          <Text
            isSmallWindowHeight={isSmallWindowHeight}
            style={{ marginBottom: 0 }}
            roomStyles={roomStyles}
          >
            {t("Insert a link to add a PDF")}
          </Text>
          <InputWrapper
            isSmallWindowHeight={isSmallWindowHeight}
            style={{ alignItems: "normal" }}
          >
            <CommonInput
              type="text"
              fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
              height={isSmallWindowHeight ? "2rem" : "2.5rem"}
              placeholder="https://..."
              value={whiteboardWebsiteLink}
              onChange={e => setWhiteboardWebsiteLink(e.target.value)}
              id={"watchid"}
              error={whiteboardLinkError}
              style={{ alignItems: "normal" }}
            />
          </InputWrapper>
          <SecondaryButton
            onClick={addPDFToWhiteboard}
            marginBottom={isSmallWindowHeight ? "0.3rem" : "0.4rem"}
            fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
            height={isSmallWindowHeight ? "2rem" : "2.5rem"}
            disabled={whiteboardWebsiteLink.length === 0}
          >
            {/* if loading, show spinner and percentage */}
            <Flex alignItems={"center"}>
              {t("Add PDF")}
              {fileUploadProgress != null && (
                <Flex alignItems="center" paddingLeft={"5px"}>
                  <Text
                    isSmallWindowHeight={isSmallWindowHeight}
                    roomStyles={roomStyles}
                    style={{ marginBottom: 0 }}
                  >
                    {fileUploadProgress}%
                  </Text>
                  <Spinner size="xs" marginLeft={"5px"} />
                </Flex>
              )}
            </Flex>
          </SecondaryButton>
        </>
      )}
    </Wrapper>
  )
}

export { StartScreenButtons }
